// React Router v7 doesn't export json, we'll create our own helper
function json<T>(data: T, init?: ResponseInit): Response {
  return new Response(JSON.stringify(data), {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...init?.headers,
    },
  });
}

/**
 * Standard error response format
 */
export interface ErrorResponse {
  success: false;
  error: {
    code: string;
    message: string;
    details?: any;
  };
}

/**
 * Custom error classes for different error types
 */
export class AppError extends Error {
  constructor(
    public code: string,
    message: string,
    public status: number = 500,
    public details?: any
  ) {
    super(message);
    this.name = 'AppError';
  }
}

export class ValidationError extends AppError {
  constructor(message: string, details?: any) {
    super('VALIDATION_ERROR', message, 400, details);
    this.name = 'ValidationError';
  }
}

export class AuthenticationError extends AppError {
  constructor(message: string = 'Authentication required') {
    super('AUTH_ERROR', message, 401);
    this.name = 'AuthenticationError';
  }
}

export class AuthorizationError extends AppError {
  constructor(message: string = 'Access denied') {
    super('FORBIDDEN', message, 403);
    this.name = 'AuthorizationError';
  }
}

export class NotFoundError extends AppError {
  constructor(resource: string = 'Resource') {
    super('NOT_FOUND', `${resource} not found`, 404);
    this.name = 'NotFoundError';
  }
}

export class RateLimitError extends AppError {
  constructor(message: string = 'Too many requests') {
    super('RATE_LIMIT', message, 429);
    this.name = 'RateLimitError';
  }
}

export class ServerError extends AppError {
  constructor(message: string = 'Internal server error', details?: any) {
    super('SERVER_ERROR', message, 500, details);
    this.name = 'ServerError';
  }
}

/**
 * Helper to create error responses for loaders/actions
 */
export function errorResponse(error: AppError | Error) {
  if (error instanceof AppError) {
    return json<ErrorResponse>(
      {
        success: false,
        error: {
          code: error.code,
          message: error.message,
          details: error.details,
        },
      },
      { status: error.status }
    );
  }

  // Generic error
  return json<ErrorResponse>(
    {
      success: false,
      error: {
        code: 'UNKNOWN_ERROR',
        message: error.message || 'An unexpected error occurred',
      },
    },
    { status: 500 }
  );
}

/**
 * Helper to handle API errors in loaders/actions
 */
export function handleApiError(error: any): never {
  console.error('API Error:', error);

  if (error.status === 401) {
    throw new AuthenticationError(error.body?.error?.message);
  }

  if (error.status === 403) {
    throw new AuthorizationError(error.body?.error?.message);
  }

  if (error.status === 404) {
    throw new NotFoundError();
  }

  if (error.status === 429) {
    throw new RateLimitError(error.body?.error?.message);
  }

  if (error.status >= 500) {
    throw new ServerError(
      error.body?.error?.message || 'Server error',
      error.body?.error?.details
    );
  }

  throw new AppError(
    error.body?.error?.code || 'API_ERROR',
    error.body?.error?.message || 'API request failed',
    error.status || 500,
    error.body?.error?.details
  );
}

/**
 * Safely parse error messages from various sources
 */
export function getErrorMessage(error: unknown): string {
  if (typeof error === 'string') {
    return error;
  }

  if (error instanceof Error) {
    return error.message;
  }

  if (error && typeof error === 'object') {
    if ('message' in error && typeof error.message === 'string') {
      return error.message;
    }

    if ('error' in error && typeof error.error === 'object') {
      const err = error.error as any;
      if (err.message) return err.message;
    }

    if ('body' in error && typeof error.body === 'object') {
      const body = error.body as any;
      if (body.error?.message) return body.error.message;
      if (body.message) return body.message;
    }
  }

  return 'An unexpected error occurred';
}

/**
 * Error logging utility
 */
export function logError(error: unknown, context?: Record<string, any>) {
  const timestamp = new Date().toISOString();
  const errorMessage = getErrorMessage(error);
  
  const errorLog: any = {
    timestamp,
    message: errorMessage,
    context,
    url: typeof window !== 'undefined' ? window.location.href : undefined,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
  };

  if (error instanceof Error) {
    errorLog['stack'] = error.stack;
    errorLog['name'] = error.name;
  }

  if (error instanceof AppError) {
    errorLog['code'] = error.code;
    errorLog['status'] = error.status;
    errorLog['details'] = error.details;
  }

  console.error('Error Log:', errorLog);

  // In production, send to error tracking service
  if (typeof window !== 'undefined' && window.location.hostname !== 'localhost') {
    // Send to error tracking service
    // e.g., Sentry, LogRocket, etc.
  }
}